import { FlexColumn, FlexRow, MaterialIcon, StyledButton } from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { Button } from 'baseui/button';
import { FormControl } from 'baseui/form-control';
import { MaskedInput } from 'baseui/input';
import { ListItem, ListItemLabel } from 'baseui/list';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'baseui/modal';
import { COUNTRIES, PhoneInput, StyledFlag } from 'baseui/phone-input';
import { HeadingXSmall } from 'baseui/typography';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useStyles } from '../../hooks/useStyles';
import { formatPhone } from '../../utils/strings';

function CustomFlag(props: any) {
  const { children, ...rest } = props;
  return (
    <StyledFlag
      iso={props.$iso}
      {...rest}
    />
  );
}

type Props = Readonly<{
  title: string;
  onClose: (phoneNumbers?: string[]) => void;
  isOpen: boolean;
  phoneNumbers?: string[];
}>;

function MultipleInternationalPhoneModal({ title, isOpen, onClose, phoneNumbers }: Props) {
  const { theme, classes, css } = useStyles();
  const [value, setValue] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isVisited, setIsVisited] = useState(false);
  const [country, setCountry] = useState(COUNTRIES.MX);
  const [updatedNumbers, setUpdatedNumbers] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const shouldShowError = !isValid && isVisited;
  const errorMsg = shouldShowError ? 'Ingrese un número valido' : undefined;

  const validatePhone = (phoneValue: string) => {
    if (!phoneValue) {
      return false;
    }
    const phone = phoneValue.replace(country.dialCode, '').replace(/\D/g, '').trim();
    return phone.length >= 10;
  };

  const focusInput = () => {
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) {
        return;
      }
      const cursorPosition = country.dialCode.length;
      input.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  useEffect(() => {
    setValue('');
  }, [country]);

  useEffect(() => {
    setIsValid(validatePhone(value));
    if (value === '') {
      focusInput();
    }
  }, [value, country.dialCode]);

  useEffect(() => {
    setUpdatedNumbers(phoneNumbers ? [...phoneNumbers] : []);
  }, [phoneNumbers]);

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = event.currentTarget.value;
    if (!inputValue) {
      setValue('');
      return;
    }
    setValue(`+${inputValue.replace(/\D/g, '').trim()}`);
  };

  const onAddPhone = () => {
    setUpdatedNumbers((prev) => [...prev, value]);
    setValue('');
  };

  const onRemovePhone = (index: number) => {
    setUpdatedNumbers((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
  };

  const resetModal = (saveData = false) => {
    if (saveData) {
      onClose(Array.from(new Set(updatedNumbers)));
    } else {
      onClose();
    }

    setCountry(COUNTRIES.MX);
    setIsValid(false);
    setIsVisited(false);
    setValue('');
  };

  return createPortal(
    <Modal
      animate
      closeable
      onClose={() => onClose()}
      isOpen={isOpen}
      size="auto"
    >
      <ModalHeader>
        <HeadingXSmall margin={0}>{title}</HeadingXSmall>
      </ModalHeader>
      <ModalBody>
        <FlexColumn>
          <ul
            className={css({
              width: '100%',
              minWidth: '350px',
              paddingLeft: 0,
              paddingRight: 0,
              margin: 0,
              maxHeight: '40vh',
              overflowY: 'auto',
            })}
          >
            {updatedNumbers.map((phoneNumber, index) => (
              <ListItem
                key={`phone-${index}`}
                overrides={{
                  Content: {
                    style: {
                      minHeight: '40px',
                      marginLeft: 0,
                      paddingRight: 0,
                    },
                  },
                }}
                endEnhancer={() => (
                  <Button
                    size="mini"
                    kind="tertiary"
                    shape="square"
                    onClick={() => onRemovePhone(index)}
                  >
                    <MaterialIcon
                      name="delete"
                      size="mini"
                    />
                  </Button>
                )}
              >
                <ListItemLabel
                  overrides={{
                    LabelRoot: {
                      style: {
                        alignItems: 'center',
                        paddingBottom: 0,
                        paddingTop: 0,
                      },
                    },
                  }}
                >
                  {formatPhone(phoneNumber)}
                </ListItemLabel>
              </ListItem>
            ))}
          </ul>

          <FormControl
            error={errorMsg}
            overrides={{
              LabelContainer: {
                style: {
                  marginTop: 0,
                  marginBottom: 0,
                },
              },
              ControlContainer: {
                style: {
                  marginBottom: 0,
                },
              },
              Caption: {
                style: {
                  marginTop: theme.sizing.scale200,
                  marginBottom: 0,
                },
              },
            }}
          >
            <FlexRow gap={0}>
              <PhoneInput
                country={country}
                onCountryChange={({ option }) => {
                  setCountry(option as typeof country);
                }}
                overrides={{
                  Input: () => null,
                  FlagContainer: {
                    component: CustomFlag,
                  },
                }}
                size="compact"
              />
              <MaskedInput
                inputRef={inputRef}
                value={value}
                placeholder="##########"
                onChange={onInputChange}
                mask={`${country.dialCode} 999-999-9999`}
                size="compact"
                error={shouldShowError}
                onBlur={() => setIsVisited(true)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && isValid) {
                    onAddPhone();
                  }
                }}
              />
              <Block
                style={{
                  marginLeft: theme.sizing.scale300,
                }}
              >
                <Button
                  size="compact"
                  kind="tertiary"
                  shape="square"
                  disabled={!isValid}
                  onClick={onAddPhone}
                >
                  <MaterialIcon
                    name="check_circle"
                    size="compact"
                  />
                </Button>
              </Block>
            </FlexRow>
          </FormControl>
        </FlexColumn>
      </ModalBody>
      <ModalFooter>
        <FlexRow
          gap={theme.sizing.scale300}
          classNames={classes.centeredEnd}
        >
          <StyledButton
            kind="tertiary"
            onClick={() => resetModal()}
          >
            Cancelar
          </StyledButton>
          <StyledButton onClick={() => resetModal(true)}>Aceptar</StyledButton>
        </FlexRow>
      </ModalFooter>
    </Modal>,
    document.body,
  );
}

export default MultipleInternationalPhoneModal;
